/** Settings, staff, tills and the activity log. */
import { useCallback, useEffect, useState } from 'react';
import type { Permission, RoleRecord, StoreSettings } from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useAuth } from '../../core/state/auth.js';
import { useToast } from '../../core/state/toast.js';
import { api } from '../../core/api.js';
import { deviceId } from '../../core/device.js';
import { Button, Confirm, Empty, Field, Loading, Modal, Pill, Tabs } from '../../core/ui/ui.js';
import { RangePicker, useDateRange } from '../../core/ui/bits.js';
import { StorageCard } from '../../core/ui/StorageNotice.js';
import { PrintingSection } from './Printing.js';
import { auditDetail, auditLabel } from '../../core/auditWords.js';
import { ACCENT_PRESETS, accentShades, DEFAULT_ACCENT, parseHex } from '@pos/shared';
import { applyTheme, resolveMode } from '../../core/theme.js';
import { UserPermissionEditor } from './Roles.js';
import { LogoPicker } from './Setup.js';

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

type Tab = 'store' | 'appearance' | 'till' | 'currencies' | 'tax' | 'receipt' | 'printing' | 'exchanges' | 'backups';

export function Settings() {
  const t = useT();
  const { can, hasFeature } = useAuth();
  const [tab, setTab] = useState<Tab>('store');

  // Backups are their own permission; showing the tab to somebody who cannot
  // run one only produces a refusal when they press the button.
  const tabs: { key: Tab; label: string }[] = [
    { key: 'store', label: t('settings.store') },
    { key: 'appearance', label: t('settings.appearance') },
    { key: 'till', label: t('settings.tillTab') },
    ...(can('settings.currencies') ? [{ key: 'currencies' as Tab, label: t('settings.currencies') }] : []),
    { key: 'tax', label: t('settings.vat') },
    { key: 'receipt', label: t('settings.receipt') },
    { key: 'printing', label: t('print.title') },
    ...(hasFeature('exchanges') ? [{ key: 'exchanges' as Tab, label: t('settings.exchanges') }] : []),
    ...(can('backup.run') ? [{ key: 'backups' as Tab, label: t('settings.backups') }] : []),
  ];

  return (
    <div className="col" style={{ maxWidth: 820 }}>
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === 'store' ? <StoreSection /> : null}
      {tab === 'appearance' ? <AppearanceSection /> : null}
      {tab === 'till' ? <TillSection /> : null}
      {tab === 'currencies' ? <CurrencySection /> : null}
      {tab === 'tax' ? <TaxSection /> : null}
      {tab === 'receipt' ? <ReceiptSection /> : null}
      {tab === 'printing' ? <PrintingSection /> : null}
      {tab === 'exchanges' ? <ExchangeSection /> : null}
      {tab === 'backups' ? <BackupSection /> : null}
    </div>
  );
}

/** A settings form that only enables Save once something actually changed. */
export function useDraft() {
  // As saved, not as the till applies it: a switch whose module is off must
  // still be saved exactly as the shop left it.
  const { rawSettings: settings, save } = useSettings();
  const toast = useToast();
  const t = useT();
  const [draft, setDraft] = useState<StoreSettings>(settings);
  const [busy, setBusy] = useState(false);

  useEffect(() => setDraft(settings), [settings]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const commit = async () => {
    setBusy(true);
    try {
      await save(draft);
      toast.ok(t('common.saved'));
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return { draft, setDraft, dirty, busy, commit };
}

export function SaveBar({ dirty, busy, onSave }: { dirty: boolean; busy: boolean; onSave: () => void }) {
  const t = useT();
  const { can } = useAuth();
  // Someone who may only look at the settings gets no Save button at all,
  // rather than one that fails when pressed.
  if (!can('settings.edit')) {
    return <p className="xsmall muted" style={{ marginBlockStart: 'var(--s-4)' }}>{t('settings.readOnly')}</p>;
  }
  return (
    <div className="row" style={{ marginBlockStart: 'var(--s-5)' }}>
      <div className="spacer" />
      <Button variant="primary" disabled={!dirty} busy={busy} onClick={onSave}>
        {t('common.save')}
      </Button>
    </div>
  );
}

function StoreSection() {
  const t = useT();
  const { draft, setDraft, dirty, busy, commit } = useDraft();
  return (
    <div className="card">
      <h2>{t('settings.store')}</h2>
      <div className="grid c2" style={{ marginBlockStart: 'var(--s-4)' }}>
        <Field label={`${t('settings.storeName')} (العربية)`}>
          <input value={draft.storeNameAr} onChange={(e) => setDraft({ ...draft, storeNameAr: e.target.value })} dir="rtl" />
        </Field>
        <Field label={`${t('settings.storeName')} (English)`}>
          <input value={draft.storeNameEn} onChange={(e) => setDraft({ ...draft, storeNameEn: e.target.value })} dir="ltr" />
        </Field>
        <Field label={t('settings.address')}>
          <input value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
        </Field>
        <Field label={t('common.phone')}>
          <input type="tel" dir="ltr" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} />
        </Field>
        <Field label={t('settings.taxNumber')}>
          <input value={draft.taxNumber} onChange={(e) => setDraft({ ...draft, taxNumber: e.target.value })} className="tnum" />
        </Field>
        <Field label={t('settings.arabicDigits')}>
          <label className="checkline">
            <input
              type="checkbox"
              checked={draft.arabicDigits}
              onChange={(e) => setDraft({ ...draft, arabicDigits: e.target.checked })}
            />
            ٠١٢٣٤٥٦٧٨٩
          </label>
        </Field>
      </div>

      <Field label={t('settings.logo')} hint={t('setup.logoHint')}>
        <LogoPicker value={draft.logoDataUrl ?? null} onChange={(logoDataUrl) => setDraft({ ...draft, logoDataUrl })} />
      </Field>

      <SaveBar dirty={dirty} busy={busy} onSave={() => void commit()} />
    </div>
  );
}

function CurrencySection() {
  const t = useT();
  const { can } = useAuth();
  const { currencies, saveRate } = useSettings();
  const toast = useToast();
  const [rates, setRates] = useState<Record<string, string>>({});

  useEffect(() => {
    setRates(Object.fromEntries(currencies.map((c) => [c.code, (c.rateMicros / 1_000_000).toFixed(4)])));
  }, [currencies]);

  return (
    <div className="card">
      <h2>{t('settings.currencies')}</h2>
      <p className="small muted">{t('settings.rateNote')}</p>

      <div className="col" style={{ marginBlockStart: 'var(--s-4)' }}>
        {currencies.map((c) => (
          <div className="row" key={c.code} style={{ alignItems: 'flex-end' }}>
            <div style={{ minWidth: 90 }}>
              <div className="strong">{c.symbol} {c.code}</div>
              <div className="xsmall muted">{c.decimals} decimals</div>
            </div>
            {c.code === 'ILS' ? (
              <Pill kind="accent">{t('common.total')}</Pill>
            ) : (
              <>
                <Field label={t('settings.ratePerUnit', { code: c.code })}>
                  <input
                    type="number"
                    step="0.0001"
                    className="tnum"
                    value={rates[c.code] ?? ''}
                    onChange={(e) => setRates({ ...rates, [c.code]: e.target.value })}
                    style={{ width: 140 }}
                  />
                </Field>
                {can('settings.currencies') ? (
                  <Button
                    onClick={() =>
                      void saveRate(c.code, Number(rates[c.code]))
                        .then(() => toast.ok(t('settings.rateSaved')))
                        .catch((e: Error) => toast.err(e.message))
                    }
                  >
                    {t('common.save')}
                  </Button>
                ) : null}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TaxSection() {
  const t = useT();
  const { draft, setDraft, dirty, busy, commit } = useDraft();
  return (
    <div className="card">
      <h2>{t('settings.vat')}</h2>
      <p className="small muted">{t('settings.vatNote')}</p>
      <div className="grid c2" style={{ marginBlockStart: 'var(--s-4)' }}>
        <Field label={t('settings.vatRate')}>
          <input
            type="number"
            step="0.5"
            value={draft.vatRate}
            onChange={(e) => setDraft({ ...draft, vatRate: Number(e.target.value) })}
            className="tnum"
          />
        </Field>
        <Field label={t('settings.cashStep')} hint={t('pay.roundingNote')}>
          <input
            type="number"
            value={draft.cashStepAgorot}
            onChange={(e) => setDraft({ ...draft, cashStepAgorot: Number(e.target.value) })}
            className="tnum"
          />
        </Field>
      </div>
      <SaveBar dirty={dirty} busy={busy} onSave={() => void commit()} />
    </div>
  );
}

function ReceiptSection() {
  const t = useT();
  const { draft, setDraft, dirty, busy, commit } = useDraft();
  return (
    <div className="card">
      <h2>{t('settings.receipt')}</h2>
      <Field label={t('settings.receiptLang')}>
        <div className="btn-row">
          {(['ar', 'en', 'both'] as const).map((v) => (
            <Button
              key={v}
              variant={draft.receiptLang === v ? 'primary' : 'default'}
              onClick={() => setDraft({ ...draft, receiptLang: v })}
            >
              {v === 'both' ? t('settings.receiptBoth') : v === 'ar' ? 'العربية' : 'English'}
            </Button>
          ))}
        </div>
      </Field>
      <Field label={`${t('settings.receiptFooter')} (العربية)`}>
        <textarea
          value={draft.receiptFooterAr}
          onChange={(e) => setDraft({ ...draft, receiptFooterAr: e.target.value })}
          dir="rtl"
        />
      </Field>
      <Field label={`${t('settings.receiptFooter')} (English)`}>
        <textarea
          value={draft.receiptFooterEn}
          onChange={(e) => setDraft({ ...draft, receiptFooterEn: e.target.value })}
          dir="ltr"
        />
      </Field>
      <SaveBar dirty={dirty} busy={busy} onSave={() => void commit()} />
    </div>
  );
}

/**
 * What the till does, and what it leaves alone.
 *
 * Nothing here deletes anything. Switching a part off hides it from the menu
 * and from the till; every shift already counted and every sale ever held stays
 * in the database, and ticking the box brings the screens straight back.
 */
function TillSection() {
  const t = useT();
  const { hasFeature } = useAuth();
  const { draft, setDraft, dirty, busy, commit } = useDraft();

  const toggle = (key: keyof StoreSettings, title: string, desc?: string) => (
    <label className="switchrow" key={String(key)}>
      <div className="grow">
        <div className="t">{title}</div>
        {desc ? <div className="d">{desc}</div> : null}
      </div>
      <input
        type="checkbox"
        checked={!!draft[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })}
        style={{ width: 22, height: 22, accentColor: 'var(--accent)' }}
      />
    </label>
  );

  return (
    <div className="card">
      <h2>{t('settings.tillTab')}</h2>
      <p className="small muted">{t('settings.tillNote')}</p>
      <div style={{ marginBlockStart: 'var(--s-4)' }}>
        {hasFeature('shifts') ? toggle('shiftsEnabled', t('settings.useShifts'), t('settings.useShiftsNote')) : null}
        {toggle('holdSalesEnabled', t('settings.useHold'), t('settings.useHoldNote'))}
        {hasFeature('customers') ? toggle('customerTabsEnabled', t('settings.customerTabs')) : null}
      </div>
      <div style={{ marginBlockStart: 'var(--s-4)' }}>
      <Field label={t('settings.idleSignOut')} hint={t('settings.idleSignOutNote')}>
        <select
          value={draft.idleSignOutMinutes ?? 0}
          onChange={(e) => setDraft({ ...draft, idleSignOutMinutes: Number(e.target.value) })}
        >
          <option value={0}>{t('settings.idleNever')}</option>
          {[15, 30, 60, 120].map((m) => (
            <option key={m} value={m}>
              {t('settings.idleAfter', { n: m })}
            </option>
          ))}
        </select>
      </Field>
      </div>
      <SaveBar dirty={dirty} busy={busy} onSave={() => void commit()} />
    </div>
  );
}

function ExchangeSection() {
  const t = useT();
  const { draft, setDraft, dirty, busy, commit } = useDraft();

  // A label, not a div: tapping anywhere on the row toggles it, which on a
  // tablet is the difference between usable and fiddly.
  const toggle = (key: keyof StoreSettings, title: string, desc?: string) => (
    <label className="switchrow" key={String(key)}>
      <div className="grow">
        <div className="t">{title}</div>
        {desc ? <div className="d">{desc}</div> : null}
      </div>
      <input
        type="checkbox"
        checked={!!draft[key]}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })}
        style={{ width: 22, height: 22, accentColor: 'var(--accent)' }}
      />
    </label>
  );

  return (
    <div className="card">
      <h2>{t('settings.exchanges')}</h2>
      <div className="grid c2" style={{ marginBlockStart: 'var(--s-4)' }}>
        <Field label={t('settings.exchangeWindow')}>
          <input
            type="number"
            value={draft.exchangeWindowDays}
            onChange={(e) => setDraft({ ...draft, exchangeWindowDays: Number(e.target.value) })}
            className="tnum"
          />
        </Field>
        <Field label={t('settings.noReceiptLookback')}>
          <input
            type="number"
            value={draft.noReceiptLookbackDays}
            onChange={(e) => setDraft({ ...draft, noReceiptLookbackDays: Number(e.target.value) })}
            className="tnum"
          />
        </Field>
        <Field label={t('settings.maxDiscount')}>
          <input
            type="number"
            value={draft.maxCashierDiscountPercent}
            onChange={(e) => setDraft({ ...draft, maxCashierDiscountPercent: Number(e.target.value) })}
            className="tnum"
          />
        </Field>
        <Field label={t('settings.lowStockDefault')}>
          <input
            type="number"
            value={draft.lowStockDefault}
            onChange={(e) => setDraft({ ...draft, lowStockDefault: Number(e.target.value) })}
            className="tnum"
          />
        </Field>
        <Field label={t('settings.deadStockDays')}>
          <input
            type="number"
            value={draft.deadStockDays}
            onChange={(e) => setDraft({ ...draft, deadStockDays: Number(e.target.value) })}
            className="tnum"
          />
        </Field>
        <Field label={t('settings.unsyncedWarn')}>
          <input
            type="number"
            value={draft.unsyncedWarnHours}
            onChange={(e) => setDraft({ ...draft, unsyncedWarnHours: Number(e.target.value) })}
            className="tnum"
          />
        </Field>
      </div>

      <div style={{ marginBlockStart: 'var(--s-4)' }}>
        {toggle('allowCashRefund', t('settings.allowCashRefund'), t('settings.allowCashRefundNote'))}
        {toggle('enforceFinalSale', t('settings.enforceFinalSale'))}
      </div>

      <SaveBar dirty={dirty} busy={busy} onSave={() => void commit()} />
    </div>
  );
}

function BackupSection() {
  const t = useT();
  const { can } = useAuth();
  const { dateTime } = useSettings();
  const toast = useToast();
  const [rows, setRows] = useState<{ name: string; folder: string; takenAt: string; bytes: number }[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await api('/admin/backups'));
    } catch {
      /* nothing yet */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="col">
      <div className="card">
        <h2>{t('settings.backups')}</h2>
        <p className="small muted">{t('settings.backupNote')}</p>
        <div className="btn-row" style={{ marginBlockStart: 'var(--s-4)' }}>
          <Button
            variant="primary"
            busy={busy}
            onClick={() => {
              setBusy(true);
              void api('/admin/backups', { body: {}, timeoutMs: 120_000 })
                .then(() => {
                  toast.ok(t('settings.backupMade'));
                  void load();
                })
                .catch((e: Error) => toast.err(e.message))
                .finally(() => setBusy(false));
            }}
          >
            {t('settings.backupNow')}
          </Button>
          {can('settings.edit') ? (
            <Button
              onClick={() =>
                void api('/admin/rebuild-stock', { body: {} })
                  .then(() => toast.ok(t('settings.rebuilt')))
                  .catch((e: Error) => toast.err(e.message))
              }
            >
              {t('settings.rebuildStock')}
            </Button>
          ) : null}
        </div>
        <p className="xsmall muted" style={{ marginBlockStart: 'var(--s-2)' }}>{t('settings.rebuildNote')}</p>
      </div>

      {rows.length > 0 ? (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('common.date')}</th>
                  <th>{t('common.name')}</th>
                  <th className="num">MB</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.name}>
                    <td data-label={t('common.date')}>{dateTime(r.takenAt)}</td>
                    <td className="xsmall muted">{r.folder}</td>
                    <td className="num tnum">{(r.bytes / 1024 / 1024).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Staff
// ---------------------------------------------------------------------------

interface StaffRow {
  id: string;
  username: string;
  name: string;
  role: string;
  role_id: string | null;
  role_name_en: string | null;
  role_name_ar: string | null;
  max_discount_percent: number;
  active: number;
  has_password: number;
  permissionCount: number;
  overrides: { granted: Permission[]; revoked: Permission[] };
}

export function Users() {
  const t = useT();
  const { lang } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState<StaffRow[]>([]);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<StaffRow> | null>(null);
  const [requests, setRequests] = useState<{ id: string; user_id: string; username: string; name: string; message: string; created_at: string }[]>([]);
  const { dateTime } = useSettings();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r, q] = await Promise.all([
        api<StaffRow[]>('/admin/users'),
        api<RoleRecord[]>('/admin/roles'),
        api<{ id: string; user_id: string; username: string; name: string; message: string; created_at: string }[]>(
          '/admin/password-requests',
        ).catch(() => []),
      ]);
      setRows(u);
      setRoles(r);
      setRequests(q);
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Loading />;

  return (
    <div className="col">
      <div className="row">
        <h2>{t('settings.staff')}</h2>
        <div className="spacer" />
        {can('users.edit') ? (
          <Button variant="primary" onClick={() => setEditing({})}>
            {t('settings.addUser')}
          </Button>
        ) : null}
      </div>

      {requests.length ? (
        <div className="card col">
          <h3 style={{ margin: 0 }}>{t('forgot.requestsTitle')}</h3>
          {requests.map((q) => {
            const row = rows.find((r) => r.id === q.user_id);
            return (
              <div key={q.id} className="row wrap" style={{ gap: 'var(--s-3)', alignItems: 'center' }}>
                <div className="grow" style={{ minWidth: '14rem' }}>
                  <div className="strong">
                    {q.name} <span className="muted" dir="ltr">({q.username})</span>
                  </div>
                  <div className="xsmall muted">
                    {dateTime(q.created_at)}
                    {q.message ? ` · ${q.message}` : ''}
                  </div>
                </div>
                {can('users.edit') && row ? (
                  <Button size="sm" variant="primary" onClick={() => setEditing(row)}>
                    {t('forgot.setNew')}
                  </Button>
                ) : null}
                {can('users.edit') ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      void api(`/admin/password-requests/${q.id}/dismiss`, { body: {} }).then(() => load())
                    }
                  >
                    {t('forgot.dismiss')}
                  </Button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="card flush">
        <div className="table-wrap">
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('login.username')}</th>
                <th>{t('roles.role')}</th>
                <th>{t('roles.forOnePerson')}</th>
                <th className="num">{t('settings.maxDiscountUser')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const added = r.overrides.granted.length;
                const removed = r.overrides.revoked.length;
                return (
                  <tr key={r.id}>
                    <td className="strong" data-label={t('common.name')}>
                      {r.name}
                      <div className="xsmall muted">{t('roles.permissionsTotal', { n: r.permissionCount })}</div>
                    </td>
                    <td data-label={t('login.username')}>{r.username}</td>
                    <td data-label={t('roles.role')}>
                      <Pill kind={r.role === 'owner' ? 'accent' : undefined}>
                        {(lang === 'ar' ? r.role_name_ar : r.role_name_en) ?? r.role}
                      </Pill>
                    </td>
                    <td data-label={t('roles.forOnePerson')} className="xsmall">
                      {added || removed ? (
                        <span className="muted">{t('roles.overrideSummary', { added, removed })}</span>
                      ) : (
                        <span className="muted">{t('roles.noOverrides')}</span>
                      )}
                    </td>
                    <td className="num tnum" data-label={t('settings.maxDiscountUser')}>
                      {r.max_discount_percent}%
                    </td>
                    <td className="num">
                      <div className="row" style={{ justifyContent: 'flex-end' }}>
                        {r.active === 0 ? <Pill kind="danger">{t('settings.inactive')}</Pill> : null}
                        {can('users.edit') ? (
                          <Button size="sm" onClick={() => setEditing(r)}>
                            {t('common.edit')}
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <UserEditor
          user={editing}
          roles={roles}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.ok(t('common.saved'));
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

function UserEditor({
  user,
  roles,
  onClose,
  onSaved,
}: {
  user: Partial<StaffRow>;
  roles: RoleRecord[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const isNew = !user.id;

  const [tab, setTab] = useState<'who' | 'permissions'>('who');
  const [form, setForm] = useState({
    name: user.name ?? '',
    username: user.username ?? '',
    roleId: user.role_id ?? roles.find((r) => r.key === 'cashier')?.id ?? '',
    maxDiscountPercent: user.max_discount_percent ?? 10,
    active: user.active === undefined ? true : user.active === 1,
    password: '',
  });
  const [overrides, setOverrides] = useState(user.overrides ?? { granted: [], revoked: [] });
  const [busy, setBusy] = useState(false);

  const role = roles.find((r) => r.id === form.roleId);

  // Changing the role resets the discount limit to that role default.
  const pickRole = (id: string) => {
    const r = roles.find((x) => x.id === id);
    setForm((f) => ({ ...f, roleId: id, maxDiscountPercent: r?.maxDiscountPercent ?? f.maxDiscountPercent }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const body: Record<string, unknown> = { ...form, ...overrides };
      if (!form.password) delete body.password;
      if (isNew) await api('/admin/users', { body });
      else await api(`/admin/users/${user.id}`, { method: 'PUT', body });
      onSaved();
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isNew ? t('settings.addUser') : String(user.name)}
      wide
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button
            variant="primary"
            busy={busy}
            onClick={() => void save()}
            disabled={!form.name || !form.username || !form.roleId || (isNew && !form.password)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Tabs
        tabs={[
          { key: 'who' as const, label: t('common.name') },
          { key: 'permissions' as const, label: t('roles.forOnePerson') },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'who' ? (
        <>
          <div className="grid c2">
            <Field label={t('common.name')}>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
            </Field>
            <Field label={t('login.username')}>
              <input
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                disabled={!isNew}
                dir="ltr"
              />
            </Field>
          </div>

          <Field label={t('roles.role')}>
            <div className="btn-row">
              {roles.map((r) => (
                <Button
                  key={r.id}
                  variant={form.roleId === r.id ? 'primary' : 'default'}
                  onClick={() => pickRole(r.id)}
                >
                  {lang === 'ar' ? r.nameAr : r.nameEn}
                  <span className="xsmall muted"> ({r.permissions.length})</span>
                </Button>
              ))}
            </div>
          </Field>

          <Field
            label={isNew ? t('login.password') : t('settings.newPassword')}
            hint={isNew ? t('settings.passwordNote') : t('settings.passwordKeep')}
          >
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
              dir="ltr"
            />
          </Field>

          <Field label={t('settings.maxDiscountUser')}>
            <input
              type="number"
              value={form.maxDiscountPercent}
              onChange={(e) => setForm({ ...form, maxDiscountPercent: Number(e.target.value) })}
              className="tnum"
            />
          </Field>

          {!isNew ? (
            <label className="checkline">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              {t('common.yes')}
            </label>
          ) : null}
        </>
      ) : (
        <UserPermissionEditor
          userId={String(user.id ?? '')}
          rolePermissions={role?.permissions ?? []}
          overrides={overrides}
          onChange={setOverrides}
        />
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Tills
// ---------------------------------------------------------------------------

export function Devices() {
  const t = useT();
  const { dateTime } = useSettings();
  const { can } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [limit, setLimit] = useState<{ max: number; active: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [retiring, setRetiring] = useState<Record<string, unknown> | null>(null);
  const me = deviceId();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, lim] = await Promise.all([
        api<Record<string, unknown>[]>('/admin/devices'),
        api<{ max: number; active: number }>('/admin/devices/limit'),
      ]);
      setRows(list);
      setLimit(lim);
    } finally {
      setLoading(false);
    }
  }, []);

  const setRetired = async (d: Record<string, unknown>, retire: boolean) => {
    try {
      await api(`/admin/devices/${d.id}/${retire ? 'retire' : 'restore'}`, { body: {} });
      toast.ok(retire ? t('tills.retired') : t('tills.restored'));
      await load();
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setRetiring(null);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Loading />;

  return (
    <div className="col">
      <div className="card">
        <div className="card-head" style={{ marginBlockEnd: 'var(--s-2)' }}>
          <h2>{t('settings.devices')}</h2>
          <span className="spacer" />
          {limit ? <Pill kind={limit.active >= limit.max ? 'warn' : 'ok'}>{t('tills.inUse', { n: limit.active, max: limit.max })}</Pill> : null}
        </div>
        <p className="small muted">{t('settings.deviceSeriesNote')}</p>
        {limit && limit.max > 1 ? <p className="small muted">{t('tills.limitNote')}</p> : null}
      </div>

      <StorageCard />

      <div className="card flush">
        <div className="table-wrap">
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.name')}</th>
                <th>{t('settings.deviceSeries')}</th>
                <th>{t('settings.deviceLastSeen')}</th>
                <th>{t('tills.onTill')}</th>
                <th className="num">{t('reports.transactions')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={String(r.id)} className={r.retired_at ? 'retired' : ''}>
                  <td className="strong" data-label={t('common.name')}>
                    {String(r.name)}
                    {String(r.id) === me ? <> <Pill kind="accent">{t('settings.deviceThis')}</Pill></> : null}
                    {r.retired_at ? <> <Pill>{t('tills.retiredPill')}</Pill></> : null}
                  </td>
                  <td className="tnum" data-label={t('settings.deviceSeries')}>{String(r.series_prefix)}</td>
                  <td data-label={t('settings.deviceLastSeen')}>
                    {r.last_seen_at ? dateTime(String(r.last_seen_at)) : '—'}
                  </td>
                  <td data-label={t('tills.onTill')}>
                    {Number(r.outbox_stuck) > 0 ? (
                      <Pill kind="danger">{t('tills.stuck', { n: Number(r.outbox_stuck) })}</Pill>
                    ) : Number(r.outbox_pending) > 0 ? (
                      <Pill kind="warn">{t('tills.waiting', { n: Number(r.outbox_pending) })}</Pill>
                    ) : r.last_report_at ? (
                      <Pill kind="ok">{t('tills.allUp')}</Pill>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="num tnum" data-label={t('reports.transactions')}>{Number(r.sale_count)}</td>
                  <td className="num">
                    {can('devices.edit') ? (
                      <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                        <Button size="sm" onClick={() => setEditing(r)}>
                          {t('common.edit')}
                        </Button>
                        {r.retired_at ? (
                          <Button size="sm" onClick={() => void setRetired(r, false)}>
                            {t('tills.restore')}
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => setRetiring(r)}>
                            {t('tills.retire')}
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {retiring ? (
        <Confirm
          title={t('tills.retireTitle', { till: String(retiring.name) })}
          message={
            Number(retiring.outbox_pending) + Number(retiring.outbox_stuck) > 0
              ? `${t('tills.retireWarn', { n: Number(retiring.outbox_pending) + Number(retiring.outbox_stuck) })} ${t('tills.retireMsg')}`
              : t('tills.retireMsg')
          }
          confirmLabel={t('tills.retire')}
          danger
          onConfirm={() => void setRetired(retiring, true)}
          onCancel={() => setRetiring(null)}
        />
      ) : null}

      {editing ? (
        <DeviceEditor
          device={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.ok(t('common.saved'));
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

/** Naming a till, and setting the prefix its receipts carry. */
function DeviceEditor({
  device,
  onClose,
  onSaved,
}: {
  device: Record<string, unknown>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [name, setName] = useState(String(device.name ?? ''));
  const [prefix, setPrefix] = useState(String(device.series_prefix ?? ''));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api(`/admin/devices/${device.id}`, { method: 'PUT', body: { name, seriesPrefix: prefix } });
      onSaved();
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={String(device.name)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <div className="spacer" />
          <Button variant="primary" busy={busy} disabled={!name.trim() || !prefix.trim()} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={t('common.name')}>
        <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field label={t('settings.deviceSeries')} hint={t('settings.deviceSeriesNote')}>
        <input
          value={prefix}
          onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4))}
          className="tnum"
          dir="ltr"
        />
      </Field>
      <p className="xsmall muted">{t('settings.deviceNextReceipt', { n: `${prefix}-000001` })}</p>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Activity log
// ---------------------------------------------------------------------------

export function Audit() {
  const t = useT();
  const { lang } = useI18n();
  const { dateTime, money } = useSettings();
  const { range, setRange } = useDateRange('week');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [actions, setActions] = useState<{ action: string; n: number }[]>([]);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api<{ action: string; n: number }[]>('/admin/audit/actions').then(setActions).catch(() => undefined);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api(`/admin/audit?from=${range.from}&to=${range.to}&action=${action}`));
    } finally {
      setLoading(false);
    }
  }, [range, action]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="col">
      <div className="row wrap">
        <RangePicker range={range} onChange={setRange} />
        <select value={action} onChange={(e) => setAction(e.target.value)} style={{ maxWidth: 260 }} aria-label={t('audit.what')}>
          <option value="">{t('common.all')}</option>
          {actions.map((a) => (
            <option key={a.action} value={a.action}>
              {auditLabel(a.action, lang)} ({a.n})
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty icon="🗂️" title={t('reports.noData')} />
      ) : (
        <div className="card flush">
          <div className="table-wrap">
            <table className="cards">
              <thead>
                <tr>
                  <th>{t('common.time')}</th>
                  <th>{t('common.name')}</th>
                  <th>{t('audit.what')}</th>
                  <th>{t('audit.details')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={String(r.id)}>
                    <td className="nowrap" data-label={t('common.time')}>{dateTime(String(r.created_at))}</td>
                    <td data-label={t('common.name')}>{String(r.user_name ?? '')}</td>
                    <td className="strong" data-label={t('audit.what')} title={String(r.action)}>{auditLabel(String(r.action), lang)}</td>
                    <td className="small muted" data-label={t('audit.details')} style={{ maxWidth: 420, overflowWrap: 'anywhere' }}>
                      {auditDetail(String(r.action), r.detail, lang, money)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * How the till looks.
 *
 * Both choices are applied AS THEY ARE PICKED, not on Save - choosing a colour
 * from a swatch and then hunting for the effect is no way to choose a colour.
 * Leaving without saving puts the shop's real look back.
 */
function AppearanceSection() {
  const t = useT();
  const { lang } = useI18n();
  const { settings } = useSettings();
  const { draft, setDraft, dirty, busy, commit } = useDraft();
  const { can } = useAuth();
  const editable = can('settings.edit');

  // Show the draft while this screen is open...
  useEffect(() => {
    applyTheme(draft.theme, draft.accent);
  }, [draft.theme, draft.accent]);

  // ...and put the saved look back if they wander off without saving.
  useEffect(() => {
    return () => {
      applyTheme(settings.theme, settings.accent);
    };
  }, [settings.theme, settings.accent]);

  const mode = resolveMode(draft.theme);
  const shades = accentShades(draft.accent || DEFAULT_ACCENT, mode);
  const custom = !ACCENT_PRESETS.some((p) => p.hex.toLowerCase() === (draft.accent || '').toLowerCase());

  const choices: { key: 'auto' | 'light' | 'dark'; label: string; hint: string }[] = [
    { key: 'auto', label: t('settings.themeAuto'), hint: t('settings.themeAutoHint') },
    { key: 'light', label: t('settings.themeLight'), hint: '' },
    { key: 'dark', label: t('settings.themeDark'), hint: '' },
  ];

  return (
    <div className="col">
      <div className="card">
        <h2>{t('settings.theme')}</h2>
        <p className="small muted">{t('settings.themeHint')}</p>
        <div className="btn-row" style={{ marginBlockStart: 'var(--s-4)' }}>
          {choices.map((c) => (
            <Button
              key={c.key}
              variant={draft.theme === c.key ? 'primary' : 'default'}
              disabled={!editable}
              onClick={() => setDraft({ ...draft, theme: c.key })}
            >
              {c.label}
            </Button>
          ))}
        </div>
        {draft.theme === 'auto' ? (
          <p className="xsmall muted" style={{ marginBlockStart: 'var(--s-3)' }}>
            {t('settings.themeAutoHint')} — {mode === 'dark' ? t('settings.themeDark') : t('settings.themeLight')}
          </p>
        ) : null}
      </div>

      <div className="card">
        <h2>{t('settings.accent')}</h2>
        <p className="small muted">{t('settings.accentHint')}</p>

        <div className="swatches" style={{ marginBlockStart: 'var(--s-4)' }}>
          {ACCENT_PRESETS.map((p) => {
            const on = p.hex.toLowerCase() === (draft.accent || '').toLowerCase();
            return (
              <button
                key={p.key}
                type="button"
                className={`swatch ${on ? 'on' : ''}`}
                disabled={!editable}
                title={lang === 'ar' ? p.ar : p.en}
                aria-label={lang === 'ar' ? p.ar : p.en}
                aria-pressed={on}
                style={{ background: accentShades(p.hex, mode).accent }}
                onClick={() => setDraft({ ...draft, accent: p.hex })}
              />
            );
          })}
        </div>

        <div className="row wrap" style={{ marginBlockStart: 'var(--s-5)', gap: 'var(--s-4)' }}>
          <Field label={t('settings.accentCustom')}>
            <input
              type="color"
              value={parseHex(draft.accent) ? draft.accent : DEFAULT_ACCENT}
              disabled={!editable}
              onChange={(e) => setDraft({ ...draft, accent: e.target.value })}
              style={{ width: 72, padding: 4 }}
            />
          </Field>
          {custom ? <Pill kind="accent">{draft.accent}</Pill> : null}
          <div className="spacer" />
          <Button
            variant="ghost"
            disabled={!editable || (draft.accent || '').toLowerCase() === DEFAULT_ACCENT}
            onClick={() => setDraft({ ...draft, accent: DEFAULT_ACCENT })}
          >
            {t('settings.accentReset')}
          </Button>
        </div>

        <div className="theme-demo" style={{ marginBlockStart: 'var(--s-5)' }}>
          <span className="xsmall muted">{t('settings.accentPreview')}</span>
          <div className="row wrap" style={{ gap: 'var(--s-3)', marginBlockStart: 'var(--s-2)' }}>
            <Button variant="primary">{t('common.save')}</Button>
            <Pill kind="accent">{t('settings.accent')}</Pill>
            <span
              className="small"
              style={{ background: shades.soft, color: shades.ink, padding: '6px 12px', borderRadius: 'var(--radius)' }}
            >
              {t('settings.accentPreview')}
            </span>
          </div>
        </div>

        <SaveBar dirty={dirty} busy={busy} onSave={commit} />
      </div>
    </div>
  );
}
